import { useEffect, useState } from 'react';
import { usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation } from '../../../store/backendApi';
import { Button } from '../../../components/Button';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';

export interface CommentInputProps {
  tripId: string;
  storyId: string;
  linkSecret: string | null;
}

const NAME_KEY = 'tripvapp_c_name';

export const CommentInput = (props: CommentInputProps) => {
  const { profile, isLoggedIn } = useAuthenticatedUser();

  const [name, setName] = useState<string | null>(() => {
    return localStorage.getItem(NAME_KEY) || '';
  });
  const [comment, setComment] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNameError, setShowNameError] = useState(false);

  // For non-logged-in users, they need both a name and a comment
  const needsName = !isLoggedIn && (!name || name.length < 2);
  const hasComment = comment && comment.length >= 2;
  const sendDisabled = !hasComment || isSubmitting;

  const [postComment, { isSuccess }] =
    usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation();

  const handlePostClick = () => {
    // If user needs to provide name but hasn't, show error
    if (needsName) {
      setShowNameError(true);
      // Reset after animation completes
      setTimeout(() => setShowNameError(false), 600);
      return;
    }
    submit();
  };

  const submit = async () => {
    if (!name || !comment) {
      return;
    }
    if (!isLoggedIn) {
      localStorage.setItem(NAME_KEY, name);
    }

    setIsSubmitting(true);

    const commentBodyRequest =
      isLoggedIn && profile?.userId
        ? {
          storyId: props.storyId,
          userId: profile.userId,
          body: comment,
        }
        : {
          storyId: props.storyId,
          name: name,
          body: comment,
        };

    postComment({
      tripId: props.tripId,
      linkSecret: props.linkSecret ?? 'none',
      commentBodyRequest,
    });
  };

  useEffect(() => {
    if (isLoggedIn && profile?.username) {
      setName(profile.username);
    }
  }, [isLoggedIn, profile?.username]);

  useEffect(() => {
    if (isSuccess) {
      setComment(null);
      setIsSubmitting(false);
    }
  }, [isSuccess]);

  return (
    <>
      <div className="w-full border-t border-subtle pt-3 mt-2">
        {/* Name input for non-logged-in users */}
        {!isLoggedIn && (
          <div className="mb-2">
            <style>
              {`
                @keyframes wiggle {
                  0%, 100% { transform: translateX(0); }
                  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
                  20%, 40%, 60%, 80% { transform: translateX(4px); }
                }
                .animate-wiggle {
                  animation: wiggle 0.5s ease-in-out;
                }
              `}
            </style>
            <input
              type="text"
              maxLength={30}
              value={name ?? ''}
              onChange={(e) => setName(e.target.value)}
              className={`block w-full rounded-md border-0 py-1.5 px-2 text-main shadow-sm ring-1 ring-inset placeholder:text-disabled focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                showNameError
                  ? 'ring-red-500 focus:ring-red-500 animate-wiggle'
                  : 'ring-gray-300 focus:ring-indigo-600'
              }`}
              placeholder="Your name..."
            />
          </div>
        )}

        {/* Comment input */}
        <div className="flex items-center gap-2">
          <textarea
            rows={1}
            value={comment ?? ''}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            className="block w-full resize-none border-0 bg-transparent py-1.5 px-0 text-main placeholder:text-muted focus:ring-0 sm:text-sm sm:leading-6"
            placeholder="Add a comment..."
            style={{ minHeight: '24px' }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={sendDisabled}
            onClick={handlePostClick}
            className="shrink-0 p-0 h-auto"
          >
            {isSubmitting ? '...' : 'Post'}
          </Button>
        </div>
      </div>
    </>
  );
};
